import { appConfig, auth, callable } from "../../../db.js";

import { ACCESS_CLASSES } from "../../../authRegistry.js";
import { canActivatePendingInternal } from "./pendingInternalActivation.common.js";
import { makeAttached } from "../../../utils";

export const activatePendingInternal = ({ form, pageItem, store, toast }) => {
  if (!canActivatePendingInternal({ pageItem, store })) {
    toast.error("This account is not eligible for pending activation.");
    return;
  }
  const uid = pageItem.ID;
  const email = pageItem.Basic.email;
  const name = pageItem.Basic.name;
  form.setConfig({
    title: "Activate Pending User",
    type: "function",
    functionName: "provisionInternalMember",
    description: "Enable internal membership for this pending account and send a password invitation?",
    submitButtonLabel: "Activate User",
    fields: [],
    preSubmitHook() {
      return { databaseURL: appConfig.firebaseConfig.databaseURL, uid, email, name, activatePending: true };
    },
    postSubmitHook({ functionResult }) {
      if (functionResult?.uid !== uid || functionResult.createdAuthUser !== false) throw new Error("Pending activation was not confirmed.");
      if (functionResult.invitationQueued === true) toast.success("Internal membership enabled and invitation queued.");
      else toast.warning("Internal membership enabled, but no invitation was queued.");
    },
  });
  form.toggle();
};

export const addUser = ({ form, toast }) => {
    form.setConfig({
        "title": "Add User",
    "type": "function",
    "functionName": "provisionInternalMember",
        "fields": [{
            "name": "name",
            "label": "Name",
      "type": "text",
      "required": true
        }, {
            "name": "email",
            "label": "Email",
      "type": "text",
      "required": true
        }, {
            "name": "department",
            "label": "Department",
      "type": "text",
      "required": false
        }, {
            "name": "job_title",
            "label": "Job Title",
      "type": "text",
      "required": false
    }],
    preSubmitHook() {
      return { databaseURL: appConfig.firebaseConfig.databaseURL };
    },
    postSubmitHook({ functionResult }) {
      if (!functionResult?.uid) throw new Error("No user identity was returned.");
      if (functionResult.createdAuthUser && !functionResult.invitationQueued) {
        toast.warning("Internal membership enabled, but no invitation was queued.");
      } else {
        toast.success(functionResult.invitationQueued
          ? "Internal membership enabled and invitation queued."
          : "Internal membership enabled.");
      }
    }
    });
    form.toggle();
};

export const updateUser = ({ form, db, pageItem }) => {
    form.setValues(pageItem.Basic);
    form.setConfig({
        "title": "Update User",
        "type": "update",
        "location": `Userdata/${pageItem.ID}/Basic`,
        "fields": [{
            "name": "name",
            "label": "Name",
            "type": "text"
        }, {
            "name": "department",
            "label": "Department",
            "type": "text"
        }, {
            "name": "job_title",
            "label": "Job Title",
            "type": "text"
        }, {
            "label": "Manager",
            "name": "ManagerID",
            "type": "dropdown",
            "labelKey": "name",
            "idKey": "key",
            "dataLocation": "users",
            dataFilter(user) {
                return user.main;
            }
        }, {
            "name": "main",
            "label": "Is Main User?",
            "type": "checkbox"

        }, {
            "name": "seeOnPlanner",
            "label": "See on Planner?",
            "type": "checkbox"

          }, {
            "name": "showTraining",
            "label": "Show in Training?",
            "type": "checkbox"

          }],
        postSubmitHook({ data }) {
            db.update(`users/${pageItem.ID}`, {
                "department": data.department,
                "job_title": data.job_title,
                "name": data.name,
                "main": data.main,
                "seeOnPlanner": data.seeOnPlanner,
                "showTraining": data.showTraining,
            });
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const disableUser = ({ form, pageItem, toast }) => {
  const tenantKey = appConfig.appPrefix;
    
    form.setConfig({
        "title": "Disable User",
        "description": `
      <p>Remove all memberships and permissions for this user on <strong>${tenantKey}</strong>?</p>
      <p>Profile records will be retained. Other tenant memberships and the shared sign-in account will not be disabled.</p>
      <p>If this is their only tenant, they will have no remaining tenant access.</p>
        `,
    "type": "function",
    "functionName": "syncTenantClaims",
    "submitButtonLabel": "Disable User",
    "submitButtonClass": "cancel",
    "fields": [],
    preSubmitHook() {
      return {
        uid: pageItem.ID,
        databaseURL: appConfig.firebaseConfig.databaseURL,
        claims: { [tenantKey]: null },
        confirmEmptyWipe: true,
      };
    },
    postSubmitHook({ functionResult }) {
      if (functionResult?.uid !== pageItem.ID || functionResult.updated !== true) {
        throw new Error("Tenant disablement was not confirmed.");
      }
      toast.success(`User disabled on ${tenantKey}. Other tenant memberships preserved.`);
        }
    });
    form.toggle();
};

export const editSecurityPermission = ({ category, flag, uiLabel }) => ({ form, pageItem, toast }) => {
  const uid = pageItem.ID;
  const expectedValue = pageItem.Security?.[category]?.[flag] === true;
  form.setValues({ enabled: expectedValue });
  form.setConfig({
    title: `Edit ${uiLabel || flag}`,
    type: "function",
    functionName: "updateTenantSecurity",
    fields: [{ name: "enabled", label: "Enabled", type: "toggle", required: false }],
    preSubmitHook() {
      return { uid, databaseURL: appConfig.firebaseConfig.databaseURL, category, flag, expectedValue };
    },
    postSubmitHook({ functionResult, data }) {
      if (functionResult?.uid !== uid || functionResult.category !== category || functionResult.flag !== flag
        || functionResult.updated !== true || functionResult.enabled !== data.enabled) {
        throw new Error("The permission change was not confirmed.");
      }
      toast.success("Permission updated.");
    },
  });
  form.toggle();
};

export const setDesignChangeFilter = ({ form, db, pageItem }) => {

    form.setValues({
        "DesignChangeFilter":  Object.keys(pageItem.DesignChangeFilter || {})
    })

    form.setConfig({
        "title": "Set Design Change Filter",
        "location": `Userdata/${pageItem.ID}/`,
        "type": "update",
        "fields": [{
            "name": "DesignChangeFilter",
            "label": "Design Changes",
            "type": "dropdown",
            "labelKey": "Number",
            "idKey": "ID",
            "dataLocation": "Database/DesignChanges",
            "multiple": true,
        }],
        preSubmitHook({ data }) {
            return {
                "DesignChangeFilter": makeAttached(data.DesignChangeFilter)
            };
        }

    });

    form.getDropdownData();
    form.toggle();
};

  export const impersonateUser = ({ form, router, store }) => {
    form.setConfig({
      "title": "Impersonate User",
      "type": "function",
      "functionName": "createImpersonationToken",
      "fields": [{
        "name": "uid",
        "label": "UID",
        "type": "text",
        "required": true
      }],
      preSubmitHook() {
        return {
          databaseURL: appConfig.firebaseConfig.databaseURL
        };
      },
      async postSubmitHook({ functionResult }) {
        if (!functionResult?.customToken) {
          throw new Error("Impersonation token was not returned.");
        }
        await auth.signInWithCustomToken(functionResult.customToken);
        
        // After impersonation, force token refresh to get new claims
        const user = auth.currentUser;
        if (user) {
          await user.getIdToken(true);
          const idTokenResult = await user.getIdTokenResult();
          const claims = idTokenResult?.claims || {};
          const tenantKey = appConfig.appPrefix;
          
          // Check if user has access to THIS tenant
          const tenantClaim = claims[tenantKey] || {};
          const hasNestedStructure = !!(claims.stc || claims.doa || claims.test);
          
          // Migration window: if no nested structure, check old flat keys
          const hasAccess = hasNestedStructure 
            ? !!claims[tenantKey]
            : !!(claims.internal || claims.flight_test_crew || claims.signatory || claims.signatory_military || claims.customer);
          
          if (!hasAccess) {
            alert(`Warning: Impersonated user does not have access to the ${tenantKey} tenant. You will see a limited view.`);
          }
          
          // Save impersonation state
          store.commit("save", {
            impersonation: claims.impersonation || null,
          });
        }
        
        await router.replace("/");
      }
    });
    form.toggle();
  };

/**
 * Create a pure external Flight Test Crew account (no Userdata record per spec).
 * Calls the createExternalUser callable (sets flight-test-crew claim + ensures direct-UID record).
 */
export const createFlightTestCrewAccount = async ({ form, db, store }) => {
  form.setConfig({
    title: 'Create Flight Test Crew Account (External Self-Service)',
    description: 'Creates / enables a pure external user with the flight-test-crew claim. They log into the main app and are routed to a clean private self-service view (no side nav, only their own record). No Userdata record is created.',
    type: 'custom',
    fields: [
      { name: 'email', label: 'Email', type: 'text', required: true },
      { name: 'firstName', label: 'First Name', type: 'text', required: true },
      { name: 'lastName', label: 'Last Name', type: 'text', required: true },
      { name: 'targetUid', label: 'Firebase UID (leave blank to be prompted)', type: 'text' }
    ],
    async onSubmit(data) {
      try {
        const { callable, appConfig } = await import('../../../db.js');
        const createExternal = callable('createExternalUser');
        let targetUid = data.targetUid && data.targetUid.trim();
        if (!targetUid) {
          targetUid = prompt('Enter the Firebase Auth UID for this external user (create the Auth user first in Firebase console or via extended backend):');
        }
        if (!targetUid) {
          alert('UID required');
          return;
        }
        await createExternal({ targetUid, claimType: 'flight_test_crew', firstName: data.firstName, lastName: data.lastName, databaseURL: appConfig.firebaseConfig.databaseURL });
        alert('External FTC account provisioned with claim. User can now log into the main app.');
      } catch (e) {
        console.error(e);
        alert('Provisioning failed: ' + (e.message || e));
      }
    }
  });
  form.toggle();
};

/**
 * Enable a pure external Signatory for self-service (direct UID key + signatory claim).
 * Variant-aware: Creates STC or Military signatory based on user selection.
 */
export const createSignatoryAccount = async ({ form, db, store }) => {
  form.setConfig({
    title: 'Enable Signatory Self-Service Login (External)',
    description: 'Sets the signatory claim and ensures the record is at the direct UID key (post-migration model). The user gets a clean private self-service view: read-only panels/approved functions/limitations + edit for questions, files, sign-off.',
    type: 'custom',
    fields: [
      { name: 'email', label: 'Email', type: 'text', required: true },
      { name: 'firstName', label: 'First Name', type: 'text' },
      { name: 'lastName', label: 'Last Name', type: 'text' },
      { name: 'targetUid', label: 'Firebase UID (direct key)', type: 'text' },
      { 
        name: 'variant', 
        label: 'Matrix Variant', 
        type: 'dropdown',
        dropdownData: [
          { id: 'stc', label: 'STC Signatory Matrix' },
          { id: 'military', label: 'Military Signatory Matrix' }
        ],
        required: true
      }
    ],
    async onSubmit(data) {
      try {
        const { callable, appConfig } = await import('../../../db.js');
        const createExternal = callable('createExternalUser');
        let targetUid = data.targetUid && data.targetUid.trim();
        if (!targetUid) {
          targetUid = prompt('Enter the Firebase UID that will own this signatory self-service record:');
        }
        if (!targetUid) return;
        
        // Map variant to claim type: stc -> 'signatory', military -> 'signatory_military'
        const claimType = data.variant === 'military' ? 'signatory_military' : 'signatory';
        
        await createExternal({ 
          targetUid, 
          claimType, 
          firstName: data.firstName, 
          lastName: data.lastName, 
          databaseURL: appConfig.firebaseConfig.databaseURL 
        });
        alert(`${data.variant === 'military' ? 'Military' : 'STC'} signatory self-service enabled. They can log in to the main app for their private record.`);
      } catch (e) {
        alert('Failed: ' + (e.message || e));
      }
    }
  });
  form.getDropdownData()
  form.toggle();
};

/**
 * Set tenant class for the current tenant deployment.
 * Changes the user's primary access class (internal, flight_test_crew, signatory, customer)
 * and optional extras (signatory_military, customer_id).
 * Calls syncTenantClaims Function which updates the custom token.
 */
export const setTenantClass = async ({ form, db, pageItem, store }) => {
  const tenantKey = appConfig.appPrefix; // stc, doa, or test
  
  // Note: We cannot easily read the target user's claims from the client (requires admin access).
  // The admin will need to know or look up the current state manually. A future enhancement
  // could call an admin-only callable to fetch the target user's current claims for prefill.
  
  form.setConfig({
    title: `Set ${tenantKey.toUpperCase()} Tenant Class`,
    description: `Set the primary access class for <strong>${pageItem.Basic?.name || pageItem.ID}</strong> on the <strong>${tenantKey}</strong> tenant. This updates their custom token claims. The user must refresh their token after this change (getIdToken(true)).`,
    type: 'custom',
    fields: [
      {
        name: 'primary_class',
        label: 'Primary Class',
        type: 'dropdown',
        required: true,
        dropdownData: ACCESS_CLASSES.map(ac => ({ id: ac.key, label: `${ac.key} - ${ac.description}` }))
      },
      {
        name: 'signatory_military',
        label: 'Military Signatory Variant',
        type: 'checkbox',
        default: false,
        description: 'Only applies when primary class is signatory'
      },
      {
        name: 'customer_id',
        label: 'Customer ID',
        type: 'text',
        default: '',
        description: 'Required when primary class is customer'
      }
    ],
    async onSubmit(data) {
      try {
        const primaryClass = data.primary_class;
        
        // Build the tenant claim based on primary class selection
        const tenantClaim = {
          internal: primaryClass === 'internal',
          flight_test_crew: primaryClass === 'flight_test_crew',
          signatory: primaryClass === 'signatory',
          customer: primaryClass === 'customer',
        };
        
        // Add optional extras
        if (primaryClass === 'signatory' && data.signatory_military) {
          tenantClaim.signatory_military = true;
        } else {
          // Explicitly set false to clear it if not signatory or checkbox off
          tenantClaim.signatory_military = false;
        }
        
        if (primaryClass === 'customer') {
          if (!data.customer_id || String(data.customer_id).trim() === '') {
            alert('Customer ID is required when primary class is customer');
            return;
          }
          tenantClaim.customer_id = String(data.customer_id).trim();
        }
        
        // Call syncTenantClaims Function
        const syncTenantClaims = callable('syncTenantClaims');
        await syncTenantClaims({
          uid: pageItem.ID,
          databaseURL: appConfig.firebaseConfig.databaseURL,
          claims: {
            [tenantKey]: tenantClaim
          }
        });
        
        alert(`Tenant class updated for ${tenantKey}. The user must refresh their token (getIdToken(true)) to see the changes.`);
      } catch (e) {
        console.error('Failed to update tenant class:', e);
        alert('Failed to update tenant class: ' + (e.message || e));
      }
    }
  });
  
  form.toggle();
};
